import { useParams } from 'react-router-dom';
import { useGetDocument } from '@utils/api/documents/use-get-document.tsx';
import { AudioProvider } from '@utils/providers/audio-context.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import { useGetTranscriptSession } from '@utils/api/sessions/use-get-transcript-session.tsx';
import { CaptionProvider } from '@utils/providers/caption-context.tsx';
import CaptionMain from '@components/editors/caption-editor/caption-main.tsx';
import { CaptionHotkeyProvider } from '@utils/providers/caption-hotkey-context.tsx';
import DocumentNotFound from '@components/editors/document-not-found.tsx';
import SessionNotFound from '@components/editors/session-not-found.tsx';
import BlobWarmingOverlay from '@components/editors/blob-warming-overlay.tsx';

type Params = {
  sessionId: string;
  documentId: string;
};

export default function Caption() {
  const { sessionId, documentId } = useParams<Params>();
  const {
    data: session,
    isLoading: sessionIsLoading,
    error: sessionError
  } = useGetTranscriptSession(sessionId);

  const tier = session?.result?.access_tier;
  const isWarming = tier === 'cold' || tier === 'archive';

  const {
    data: document,
    isLoading: documentIsLoading,
    error: documentError
  } = useGetDocument({
    sessionId,
    documentId,
    enabled: !!sessionId && !!documentId && !isWarming
  });

  if (sessionIsLoading || (!isWarming && documentIsLoading)) {
    return (
      <div className="w-full h-screen flex justify-center items-center align-middle">
        <Loader />
      </div>
    );
  }

  if (sessionError || !session || !sessionId) {
    return <SessionNotFound error={sessionError} />;
  }

  if (isWarming) {
    return <BlobWarmingOverlay session={session.result} />;
  }

  if (documentError || !document || !documentId) {
    return <DocumentNotFound error={documentError} />;
  }

  return (
    <AudioProvider timecodeOffset={document.result.timecode_offset}>
      <CaptionProvider
        key={documentId}
        session={session.result}
        document={document}
      >
        <CaptionHotkeyProvider>
          <CaptionMain />
        </CaptionHotkeyProvider>
      </CaptionProvider>
    </AudioProvider>
  );
}
